import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Check,
  CheckCircle2,
  Edit3,
  Loader2,
  Pencil,
  PlayCircle,
  RefreshCw,
  Sparkles,
  X,
  Zap,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * Tema de Hoje, preview do tema sincronizado da landing.
 * Permite navegar dia anterior / seguinte, regenerar (chamar IA novamente)
 * e ver claramente se o conteúdo veio da IA ou do fallback offline.
 */
export default function AdminDailyTheme() {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [calendarDays, setCalendarDays] = useState(30);
  const [batchJob, setBatchJob] = useState(null);
  const [editingDate, setEditingDate] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback((nextOffset) => {
    setLoading(true);
    api
      .get(`/admin/today?offset=${nextOffset}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const loadCalendar = useCallback((days = calendarDays) => {
    api
      .get(`/admin/daily/calendar?days=${days}`)
      .then(({ data }) => setCalendar(data))
      .catch(() => setCalendar(null));
  }, [calendarDays]);

  const loadBatchJob = useCallback(() => {
    api
      .get(`/admin/daily/batch-progress`)
      .then(({ data }) => setBatchJob(data))
      .catch(() => setBatchJob(null));
  }, []);

  useEffect(() => {
    load(offset);
  }, [offset, load]);

  useEffect(() => {
    loadCalendar(calendarDays);
  }, [loadCalendar, data, calendarDays]);

  useEffect(() => {
    loadBatchJob();
  }, [loadBatchJob]);

  // Poll batch progress quando estiver rodando
  useEffect(() => {
    if (batchJob?.status === "running") {
      pollRef.current = setInterval(() => {
        loadBatchJob();
        loadCalendar(calendarDays);
      }, 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [batchJob?.status, loadBatchJob, loadCalendar, calendarDays]);

  const startBatch = async (days) => {
    setBusy(true);
    try {
      await api.post(`/admin/daily/generate-batch?days=${days}`);
      showToast(`Geração iniciada: ${days} dias em background.`);
      loadBatchJob();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Falha ao iniciar a geração.";
      showToast(detail, false);
    } finally {
      setBusy(false);
    }
  };

  const publishDate = async (iso) => {
    try {
      await api.post(`/admin/daily/${iso}/publish`);
      showToast(`Devocional de ${iso} publicado.`);
      loadCalendar(calendarDays);
    } catch {
      showToast("Falha ao publicar.", false);
    }
  };

  const publishAllDrafts = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/daily/publish-all-drafts?days=${calendarDays}`
      );
      showToast(`${data.updated} devocional(is) publicado(s).`);
      loadCalendar(calendarDays);
    } catch {
      showToast("Falha ao publicar em lote.", false);
    } finally {
      setBusy(false);
    }
  };

  const showToast = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 3000);
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/today/regenerate?offset=${offset}`
      );
      setData(data);
      showToast(
        data.source === "ai"
          ? "Tema regenerado por IA."
          : "Tema gerado em modo fallback (IA indisponível)."
      );
    } catch {
      showToast("Falha ao regenerar.", false);
    } finally {
      setBusy(false);
    }
  };

  const clearOverride = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/today/override?offset=${offset}`);
      load(offset);
      showToast("Override removido.");
    } catch {
      showToast("Falha ao remover override.", false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="daily-theme"
      data-testid="admin-daily-theme-section"
      className="mt-14 bg-white border border-[#0B1E36]/10 p-8"
    >
      <Header offset={offset} setOffset={setOffset} onRefresh={() => load(offset)} />

      {loading ? (
        <div className="py-10 text-center text-sm text-[#0B1E36]/45">
          Carregando…
        </div>
      ) : !data ? (
        <p
          data-testid="admin-daily-theme-empty"
          className="text-sm text-[#6B7280] italic mt-6"
        >
          Não foi possível carregar o tema deste dia.
        </p>
      ) : (
        <>
          <ThemeBody
            data={data}
            onRegenerate={regenerate}
            onClearOverride={clearOverride}
            busy={busy}
          />
          <CalendarStrip
            calendar={calendar}
            currentDate={data.date}
            calendarDays={calendarDays}
            setCalendarDays={setCalendarDays}
            batchJob={batchJob}
            onStartBatch={startBatch}
            onPublishAll={publishAllDrafts}
            onPublishOne={publishDate}
            onEditOne={(iso) => setEditingDate(iso)}
            busy={busy}
            onPick={(iso) => {
              const today = new Date();
              const picked = new Date(iso + "T00:00:00");
              const diff = Math.round(
                (picked - new Date(today.toDateString())) / 86400000
              );
              setOffset(diff);
            }}
          />
        </>
      )}

      {editingDate && (
        <EditDrawer
          iso={editingDate}
          onClose={() => setEditingDate(null)}
          onSaved={() => {
            setEditingDate(null);
            load(offset);
            loadCalendar(calendarDays);
            showToast("Devocional atualizado.");
          }}
        />
      )}

      {msg && (
        <div
          data-testid="admin-daily-theme-toast"
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 text-xs uppercase tracking-[0.2em] border ${
            msg.ok
              ? "bg-emerald-50 border-emerald-300 text-emerald-800"
              : "bg-rose-50 border-rose-300 text-rose-800"
          }`}
        >
          {msg.text}
        </div>
      )}
    </section>
  );
}

function Header({ offset, setOffset, onRefresh }) {
  const labelDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return d.toLocaleDateString("pt-BR", {
      weekday: "long",
      day: "2-digit",
      month: "long",
    });
  })();
  return (
    <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
      <div className="flex items-center gap-3 text-[#C5A059]">
        <Sparkles size={16} strokeWidth={1.5} />
        <h2 className="text-[0.7rem] uppercase tracking-[0.28em] font-medium">
          Tema de Hoje
        </h2>
        <span className="text-[0.6rem] tracking-[0.22em] text-[#0B1E36]/45 uppercase ml-2 border border-[#0B1E36]/10 px-2 py-0.5">
          Sincronização IA · 24h cache
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex items-center border border-[#0B1E36]/15">
          <button
            onClick={() => setOffset(offset - 1)}
            data-testid="admin-daily-theme-prev"
            className="px-3 py-2 text-[#0B1E36]/70 hover:text-[#C5A059] hover:bg-[#FAF8F4]"
            aria-label="Dia anterior"
          >
            <ChevronLeft size={14} strokeWidth={1.5} />
          </button>
          <span
            data-testid="admin-daily-theme-date-label"
            className="px-4 py-2 text-xs uppercase tracking-[0.2em] text-[#0B1E36] min-w-[180px] text-center"
          >
            {labelDate}
            {offset === 0 && (
              <span className="ml-2 text-[#C5A059] font-medium">· hoje</span>
            )}
          </span>
          <button
            onClick={() => setOffset(offset + 1)}
            data-testid="admin-daily-theme-next"
            className="px-3 py-2 text-[#0B1E36]/70 hover:text-[#C5A059] hover:bg-[#FAF8F4]"
            aria-label="Próximo dia"
          >
            <ChevronRight size={14} strokeWidth={1.5} />
          </button>
        </div>
        {offset !== 0 && (
          <button
            onClick={() => setOffset(0)}
            data-testid="admin-daily-theme-reset"
            className="text-xs uppercase tracking-[0.22em] text-[#0B1E36]/60 hover:text-[#C5A059] px-3 py-2"
          >
            Voltar para hoje
          </button>
        )}
        <button
          onClick={onRefresh}
          data-testid="admin-daily-theme-refresh"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-[#0B1E36]/70 hover:text-[#C5A059] px-2 py-2"
        >
          <RefreshCw size={12} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

function ThemeBody({ data, onRegenerate, onClearOverride, busy }) {
  return (
    <div className="grid lg:grid-cols-12 gap-8">
      {/* Coluna esquerda, meta + ações */}
      <div className="lg:col-span-4 space-y-6">
        <div className="border border-[#0B1E36]/10 p-5 bg-[#FAFAFA]">
          <p className="text-[0.62rem] uppercase tracking-[0.28em] text-[#0B1E36]/55 font-medium mb-2">
            Sermão escolhido
          </p>
          <p
            data-testid="admin-daily-theme-sermon"
            className="font-serif-display text-[1.25rem] text-[#0B1E36] leading-[1.25]"
          >
            {data.sermon_title}
          </p>
          <p className="mt-1.5 text-[0.75rem] text-[#0B1E36]/55">
            {data.sermon_category}
            {data.sermon_verse_reference && (
              <span className="ml-2 text-[#C5A059]">
                · {data.sermon_verse_reference}
              </span>
            )}
          </p>
          <a
            href={`/pregacoes/${data.sermon_slug}`}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-block text-[0.65rem] uppercase tracking-[0.22em] text-[#0B1E36]/60 hover:text-[#C5A059] border-b border-[#0B1E36]/15 hover:border-[#C5A059] pb-0.5"
          >
            Ver sermão completo →
          </a>
        </div>

        <div className="text-[0.72rem] text-[#0B1E36]/60 space-y-1.5">
          <p>
            <CalendarDays
              size={11}
              strokeWidth={1.6}
              className="inline -mt-0.5 mr-1.5"
            />
            Data: <span className="text-[#0B1E36] font-medium">{data.date}</span>
          </p>
          <p>
            Origem:{" "}
            <span
              data-testid="admin-daily-theme-source"
              className={
                data.source === "ai"
                  ? "text-emerald-700 font-medium"
                  : "text-amber-700 font-medium"
              }
            >
              {data.source === "ai" ? "IA (Claude 4.5)" : "Fallback offline"}
            </span>
          </p>
          {data.manual_override && (
            <p className="text-[#C5A059]">⚑ Override manual ativo</p>
          )}
          {data.generated_at && (
            <p className="text-[#0B1E36]/40">
              Gerado em {data.generated_at.replace("T", " ").slice(0, 16)} UTC
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2 pt-2 border-t border-[#0B1E36]/10">
          <button
            onClick={onRegenerate}
            disabled={busy}
            data-testid="admin-daily-theme-regenerate"
            className="inline-flex items-center justify-center gap-2 bg-[#0B1E36] text-white px-4 py-2.5 text-xs uppercase tracking-[0.22em] hover:bg-[#13294B] disabled:opacity-60"
          >
            {busy ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Zap size={12} strokeWidth={1.6} />
            )}
            Regenerar com IA
          </button>
          {data.manual_override && (
            <button
              onClick={onClearOverride}
              disabled={busy}
              data-testid="admin-daily-theme-clear-override"
              className="text-xs uppercase tracking-[0.22em] text-[#0B1E36]/60 hover:text-[#C5A059] px-4 py-2 border border-[#0B1E36]/15"
            >
              Remover override
            </button>
          )}
        </div>
      </div>

      {/* Coluna direita, preview do tema */}
      <div className="lg:col-span-8 space-y-7">
        <div>
          <p className="text-[0.62rem] uppercase tracking-[0.28em] text-[#0B1E36]/55 font-medium mb-2">
            Tema do dia
          </p>
          <p
            data-testid="admin-daily-theme-summary"
            className="font-serif-display italic text-[1.4rem] md:text-[1.55rem] text-[#0B1E36] leading-[1.4]"
          >
            {data.theme_summary}
          </p>
        </div>

        {data.central_verse?.text && (
          <div>
            <p className="text-[0.62rem] uppercase tracking-[0.28em] text-[#0B1E36]/55 font-medium mb-2">
              Versículo em destaque
            </p>
            <blockquote className="font-serif-display italic text-[1.05rem] text-[#0B1E36]/85 leading-[1.6] border-l-2 border-[#C5A059] pl-4">
              “{data.central_verse.text}”
            </blockquote>
            <p className="mt-2 text-[0.72rem] uppercase tracking-[0.24em] text-[#C5A059] font-medium">
              {data.central_verse.reference}
            </p>
          </div>
        )}

        {data.devotional?.title && (
          <div>
            <p className="text-[0.62rem] uppercase tracking-[0.28em] text-[#0B1E36]/55 font-medium mb-2">
              Devocional
            </p>
            <p className="font-serif-display text-[1.15rem] text-[#0B1E36]">
              {data.devotional.title}
            </p>
            <p className="mt-2 text-[0.95rem] text-[#6B7280] leading-[1.78] font-light">
              {data.devotional.lead_paragraph}
            </p>
          </div>
        )}

        {data.phrase && (
          <div>
            <p className="text-[0.62rem] uppercase tracking-[0.28em] text-[#0B1E36]/55 font-medium mb-2">
              Frase do dia
            </p>
            <p className="font-serif-display italic text-[1.1rem] text-[#0B1E36] bg-[#0B1E36]/[0.03] px-4 py-3 border-l-2 border-[#C5A059]">
              {data.phrase}
            </p>
          </div>
        )}

        {data.prayer && (
          <div>
            <p className="text-[0.62rem] uppercase tracking-[0.28em] text-[#0B1E36]/55 font-medium mb-2">
              Oração do dia
            </p>
            <p
              data-testid="admin-daily-theme-prayer"
              className="text-[0.95rem] text-[#0B1E36]/85 leading-[1.72] font-light italic px-4 py-3 bg-[#FAF8F4] border-l-2 border-[#0B1E36]/20"
            >
              {data.prayer}
            </p>
          </div>
        )}

        {(data.related_chapters || []).length > 0 && (
          <div>
            <p className="text-[0.62rem] uppercase tracking-[0.28em] text-[#0B1E36]/55 font-medium mb-3">
              Capítulos complementares
            </p>
            <ul className="space-y-2">
              {data.related_chapters.map((c, i) => (
                <li
                  key={`${c.reference}-${i}`}
                  className="text-[0.92rem] text-[#0B1E36] flex items-baseline gap-3"
                >
                  <span className="text-[#C5A059] font-medium font-serif-display tabular-nums">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>
                    <span className="font-medium">{c.reference}</span>
                    {c.why && (
                      <span className="text-[#6B7280]">, {c.why}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {data.related_plan && (
          <div>
            <p className="text-[0.62rem] uppercase tracking-[0.28em] text-[#0B1E36]/55 font-medium mb-2">
              Plano sugerido
            </p>
            <a
              href={`/planos/${data.related_plan.slug}`}
              target="_blank"
              rel="noreferrer"
              className="inline-block text-[0.95rem] text-[#0B1E36] hover:text-[#C5A059] border-b border-[#0B1E36]/15 hover:border-[#C5A059] pb-0.5"
            >
              {data.related_plan.title} ({data.related_plan.duration_days} dias)
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

const WD_PT = {
  monday: "Seg",
  tuesday: "Ter",
  wednesday: "Qua",
  thursday: "Qui",
  friday: "Sex",
  saturday: "Sáb",
  sunday: "Dom",
};

function CalendarStrip({
  calendar,
  currentDate,
  calendarDays,
  setCalendarDays,
  batchJob,
  onStartBatch,
  onPublishAll,
  onPublishOne,
  onEditOne,
  busy,
  onPick,
}) {
  if (!calendar || !calendar.items?.length) return null;

  const drafts = calendar.items.filter((it) => it.status === "draft").length;
  const pending = calendar.items.filter((it) => it.status === "pending").length;
  const published = calendar.items.filter((it) => it.status === "published").length;

  return (
    <div
      data-testid="admin-daily-calendar"
      className="mt-10 pt-8 border-t border-[#0B1E36]/10"
    >
      {/* Controles top: range + status counts */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3 text-[#C5A059]">
          <CalendarDays size={14} strokeWidth={1.5} />
          <h3 className="text-[0.7rem] uppercase tracking-[0.28em] font-medium">
            Calendário · {calendarDays} dias
          </h3>
        </div>
        <div className="flex items-center gap-1.5" data-testid="admin-daily-range-selector">
          {[30, 90, 365].map((n) => (
            <button
              key={n}
              data-testid={`admin-daily-range-${n}`}
              onClick={() => setCalendarDays(n)}
              className={`text-[0.62rem] uppercase tracking-[0.22em] px-3 py-1.5 border transition-colors ${
                calendarDays === n
                  ? "border-[#C5A059] bg-[#C5A059] text-[#0B1E36] font-medium"
                  : "border-[#0B1E36]/15 text-[#0B1E36]/65 hover:border-[#0B1E36]/40"
              }`}
            >
              {n} dias
            </button>
          ))}
        </div>
      </div>

      <BatchPanel
        batchJob={batchJob}
        drafts={drafts}
        pending={pending}
        published={published}
        calendarDays={calendarDays}
        onStartBatch={onStartBatch}
        onPublishAll={onPublishAll}
        busy={busy}
      />

      <Legend />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5 mt-4">
        {calendar.items.map((it) => {
          const isCurrent = it.date === currentDate;
          const dayNum = parseInt(it.date.slice(-2), 10);
          const status = it.status || (it.cached ? "published" : "pending");
          return (
            <div
              key={it.date}
              data-testid={`admin-daily-calendar-day-${it.date}`}
              className={`relative text-left p-3 border transition-all group ${
                isCurrent
                  ? "bg-[#0B1E36] text-white border-[#0B1E36]"
                  : status === "draft"
                  ? "bg-amber-50/60 border-amber-300/60 hover:border-amber-500"
                  : status === "published"
                  ? "bg-[#FAF8F4] border-[#C5A059]/30 hover:border-[#C5A059]"
                  : "bg-white border-[#0B1E36]/10 hover:border-[#0B1E36]/30"
              }`}
            >
              <button
                onClick={() => onPick(it.date)}
                className="block w-full text-left"
              >
                <div className="flex items-baseline justify-between mb-1">
                  <span
                    className={`text-[0.62rem] uppercase tracking-[0.22em] ${
                      isCurrent ? "text-white/70" : "text-[#0B1E36]/55"
                    }`}
                  >
                    {WD_PT[it.weekday] || it.weekday?.slice(0, 3)} · {dayNum}
                  </span>
                  <StatusPill status={status} edited={it.edited} isCurrent={isCurrent} />
                </div>
                <p
                  className={`text-[0.78rem] leading-[1.3] line-clamp-2 ${
                    isCurrent ? "text-white" : "text-[#0B1E36]"
                  }`}
                >
                  {it.sermon_title || "·"}
                </p>
                <p
                  className={`text-[0.6rem] uppercase tracking-[0.16em] mt-1 ${
                    isCurrent ? "text-white/55" : "text-[#0B1E36]/45"
                  }`}
                >
                  {it.sermon_category || ""}
                </p>
              </button>

              {/* Action row, só para dias cached (com cache real, não pending) */}
              {it.cached && !isCurrent && (
                <div className="mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    data-testid={`admin-daily-edit-${it.date}`}
                    onClick={(e) => { e.stopPropagation(); onEditOne(it.date); }}
                    title="Editar campos"
                    className="flex-1 inline-flex items-center justify-center gap-1 text-[0.58rem] uppercase tracking-[0.18em] px-2 py-1.5 border border-[#0B1E36]/15 text-[#0B1E36]/70 hover:border-[#0B1E36] hover:text-[#0B1E36] bg-white"
                  >
                    <Pencil size={11} strokeWidth={1.6} /> Editar
                  </button>
                  {status !== "published" && (
                    <button
                      data-testid={`admin-daily-publish-${it.date}`}
                      onClick={(e) => { e.stopPropagation(); onPublishOne(it.date); }}
                      title="Marcar como publicado"
                      className="inline-flex items-center justify-center px-2 py-1.5 border border-emerald-300/50 text-emerald-700 hover:bg-emerald-50"
                    >
                      <Check size={12} strokeWidth={2} />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusPill({ status, edited, isCurrent }) {
  const cfg = {
    draft: { label: "rascunho", cls: "text-amber-700" },
    published: { label: "publicado", cls: "text-emerald-700" },
    pending: { label: "previsto", cls: "text-[#0B1E36]/35" },
  }[status] || { label: status, cls: "text-[#0B1E36]/35" };
  return (
    <span
      className={`text-[0.55rem] uppercase tracking-[0.2em] font-medium ${
        isCurrent ? "text-[#C5A059]" : cfg.cls
      }`}
    >
      {edited && status === "published" ? "✎ pub" : edited ? "✎ ed" : cfg.label}
    </span>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-4 mb-1 text-[0.58rem] uppercase tracking-[0.22em] text-[#0B1E36]/55 flex-wrap">
      <span className="flex items-center gap-1.5">
        <span className="w-2 h-2 bg-amber-300 inline-block" /> rascunho
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-2 h-2 bg-emerald-400 inline-block" /> publicado
      </span>
      <span className="flex items-center gap-1.5">
        <span className="w-2 h-2 bg-white border border-[#0B1E36]/20 inline-block" /> previsto (não gerado)
      </span>
      <span className="flex items-center gap-1.5">
        <span className="text-[#C5A059]">✎</span> editado
      </span>
    </div>
  );
}

function BatchPanel({ batchJob, drafts, pending, published, calendarDays, onStartBatch, onPublishAll, busy }) {
  const running = batchJob?.status === "running";
  const totalKnown = (batchJob?.total ?? 0);
  const totalProgress = totalKnown
    ? Math.round(((batchJob.done + batchJob.skipped) / totalKnown) * 100)
    : 0;

  return (
    <div
      data-testid="admin-daily-batch-panel"
      className="mb-5 bg-gradient-to-br from-[#0B1E36] to-[#0B1E36]/90 text-white p-5 md:p-6"
    >
      <div className="flex items-start md:items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#C5A059] mb-1">
            <Sparkles size={12} strokeWidth={1.6} />
            <span className="text-[0.6rem] uppercase tracking-[0.28em] font-medium">Geração em lote</span>
          </div>
          <div className="flex items-baseline gap-4 text-[0.78rem] flex-wrap">
            <span><strong className="text-emerald-300 text-[1rem] tabular-nums">{published}</strong> publicados</span>
            <span><strong className="text-amber-300 text-[1rem] tabular-nums">{drafts}</strong> rascunhos</span>
            <span className="opacity-70"><strong className="text-[1rem] tabular-nums">{pending}</strong> previstos</span>
            <span className="opacity-50 text-[0.7rem]">· em {calendarDays} dias</span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {drafts > 0 && (
            <button
              data-testid="admin-daily-publish-all"
              onClick={onPublishAll}
              disabled={busy || running}
              className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-[#0B1E36] font-medium px-4 py-2.5 transition-colors"
            >
              <CheckCircle2 size={13} strokeWidth={1.8} />
              Publicar {drafts} rascunho(s)
            </button>
          )}
          <button
            data-testid="admin-daily-batch-start"
            onClick={() => onStartBatch(calendarDays)}
            disabled={busy || running}
            className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] bg-[#C5A059] hover:bg-[#D4B273] disabled:opacity-50 text-[#0B1E36] font-medium px-4 py-2.5 transition-colors"
          >
            <PlayCircle size={13} strokeWidth={1.8} />
            {running ? "Gerando…" : `Gerar próximos ${calendarDays} dias`}
          </button>
        </div>
      </div>

      {running && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-[0.65rem] uppercase tracking-[0.22em] text-white/70 mb-2 tabular-nums">
            <span>{batchJob.done} gerados · {batchJob.skipped} pulados · {batchJob.failed} falhas</span>
            <span>{totalProgress}%</span>
          </div>
          <div className="h-1.5 bg-white/10 overflow-hidden">
            <div
              className="h-full bg-[#C5A059] transition-all duration-500"
              style={{ width: `${totalProgress}%` }}
            />
          </div>
          {batchJob.last_date && (
            <p className="mt-2 text-[0.6rem] tracking-[0.18em] uppercase text-white/45 tabular-nums">
              Último processado: {batchJob.last_date}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function EditDrawer({ iso, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    theme_summary: "",
    phrase: "",
    prayer: "",
    devotional_title: "",
    devotional_introduction: "",
    devotional_summary: "",
    devotional_highlight: "",
    devotional_conclusion: "",
    verse_reference: "",
    verse_text: "",
  });

  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/daily?offset=0`).catch(() => null);
    // Pega o doc específico via calendar lookup (única query simples)
    api
      .get(`/admin/daily/calendar?days=1&offset=0`)
      .catch(() => null);
    // Tenta direct fetch via offset compute
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(iso + "T00:00:00");
    const offset = Math.round((target - today) / 86400000);
    api
      .get(`/admin/daily?offset=${offset}`)
      .then(({ data }) => {
        if (cancelled) return;
        setData(data);
        setForm({
          theme_summary: data.theme_summary || "",
          phrase: data.phrase || "",
          prayer: data.prayer || "",
          devotional_title: data.devotional?.title || "",
          devotional_introduction:
            data.devotional?.introduction || data.devotional?.lead_paragraph || "",
          devotional_summary: data.devotional?.summary || "",
          devotional_highlight: data.devotional?.highlight_phrase || "",
          devotional_conclusion: data.devotional?.conclusion || "",
          verse_reference: data.central_verse?.reference || "",
          verse_text: data.central_verse?.text || "",
        });
      });
    return () => { cancelled = true; };
  }, [iso]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/daily/${iso}`, {
        theme_summary: form.theme_summary,
        phrase: form.phrase,
        prayer: form.prayer,
        devotional: {
          title: form.devotional_title,
          introduction: form.devotional_introduction,
          summary: form.devotional_summary,
          highlight_phrase: form.devotional_highlight,
          conclusion: form.devotional_conclusion,
        },
        central_verse: {
          reference: form.verse_reference,
          text: form.verse_text,
        },
      });
      onSaved();
    } catch (e) {
      console.error(e);
      alert("Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="admin-daily-edit-drawer"
      className="fixed inset-0 z-50 flex justify-end"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-[#0B1E36]/40" />
      <div
        className="relative w-full md:max-w-[680px] bg-white h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[#0B1E36]/10 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <p className="text-[0.6rem] uppercase tracking-[0.28em] text-[#C5A059] font-medium">
              Editar devocional
            </p>
            <h2 className="font-serif-display text-[1.3rem] text-[#0B1E36] mt-0.5">{iso}</h2>
          </div>
          <button
            data-testid="admin-daily-edit-close"
            onClick={onClose}
            className="text-[#0B1E36]/50 hover:text-[#0B1E36] p-2"
            aria-label="Fechar"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
        </div>

        {!data ? (
          <div className="p-8 text-center text-sm text-[#0B1E36]/50">Carregando…</div>
        ) : (
          <div className="p-6 space-y-5">
            <Field label="Tema (resumo)" value={form.theme_summary} onChange={(v) => setForm((f) => ({ ...f, theme_summary: v }))} testid="edit-theme" />
            <Field label="Versículo · referência" value={form.verse_reference} onChange={(v) => setForm((f) => ({ ...f, verse_reference: v }))} testid="edit-verse-ref" />
            <Field label="Versículo · texto" value={form.verse_text} onChange={(v) => setForm((f) => ({ ...f, verse_text: v }))} multiline rows={3} testid="edit-verse-text" />

            <div className="pt-2">
              <p className="text-[0.6rem] uppercase tracking-[0.28em] text-[#C5A059] font-medium">
                Devocional · 5 blocos
              </p>
            </div>
            <Field label="1, Título" value={form.devotional_title} onChange={(v) => setForm((f) => ({ ...f, devotional_title: v }))} testid="edit-dev-title" />
            <Field label="2, Introdução (2 a 3 parágrafos)" value={form.devotional_introduction} onChange={(v) => setForm((f) => ({ ...f, devotional_introduction: v }))} multiline rows={8} testid="edit-dev-intro" />
            <Field label="3, Resumo" value={form.devotional_summary} onChange={(v) => setForm((f) => ({ ...f, devotional_summary: v }))} multiline rows={3} testid="edit-dev-summary" />
            <Field label="4, Frase de destaque (use **palavra** para marcar 3 keywords)" value={form.devotional_highlight} onChange={(v) => setForm((f) => ({ ...f, devotional_highlight: v }))} multiline rows={2} testid="edit-dev-highlight" />
            <Field label="5, Conclusão (aplicação prática)" value={form.devotional_conclusion} onChange={(v) => setForm((f) => ({ ...f, devotional_conclusion: v }))} multiline rows={3} testid="edit-dev-conclusion" />

            <Field label="Frase do dia (autoral)" value={form.phrase} onChange={(v) => setForm((f) => ({ ...f, phrase: v }))} multiline rows={2} testid="edit-phrase" />
            <Field label="Oração do dia" value={form.prayer} onChange={(v) => setForm((f) => ({ ...f, prayer: v }))} multiline rows={5} testid="edit-prayer" />

            <div className="pt-4 flex items-center justify-end gap-2 sticky bottom-0 bg-white border-t border-[#0B1E36]/10 -mx-6 px-6 py-4">
              <button
                onClick={onClose}
                className="text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36]/65 hover:text-[#0B1E36] px-4 py-2.5"
              >
                Cancelar
              </button>
              <button
                data-testid="admin-daily-edit-save"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] bg-[#C5A059] hover:bg-[#D4B273] disabled:opacity-50 text-[#0B1E36] font-medium px-5 py-2.5"
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Edit3 size={13} strokeWidth={1.6} />}
                Salvar edições
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, multiline = false, rows = 2, testid }) {
  return (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.22em] text-[#0B1E36]/55 font-medium mb-1.5">{label}</label>
      {multiline ? (
        <textarea
          data-testid={testid}
          value={value}
          rows={rows}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-[#0B1E36]/15 focus:border-[#C5A059] outline-none px-3 py-2 text-[0.92rem] text-[#0B1E36] leading-[1.55] bg-white"
        />
      ) : (
        <input
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-[#0B1E36]/15 focus:border-[#C5A059] outline-none px-3 py-2 text-[0.92rem] text-[#0B1E36] bg-white"
        />
      )}
    </div>
  );
}
